/**
 * The shape an image needs to fill a cover slot. Game covers are shown at
 * 16:9 everywhere (giveaway cards, the details page, Steam-style headers).
 */
export interface CoverSpec {
  /** Width ÷ height. */
  aspect: number
  /** Shown to people, e.g. "16:9". */
  ratioLabel: string
  recommended: { width: number; height: number }
  /** Below this the cover looks soft on the details page. */
  minimum: { width: number; height: number }
}

export const GAME_COVER_SPEC: CoverSpec = {
  aspect: 16 / 9,
  ratioLabel: '16:9',
  recommended: { width: 1920, height: 1080 },
  minimum: { width: 1280, height: 720 },
}

export interface CropRect {
  x: number
  y: number
  width: number
  height: number
}

const MAX_UPLOAD_BYTES = 8 * 1024 * 1024

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality?: number) {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Could not encode the image'))),
      type,
      quality,
    )
  })
}

/**
 * Cuts `crop` (in the image's own pixels) out of `src` and returns it as a
 * file, scaled down to the spec's recommended width if it is larger. PNGs stay
 * PNG to keep transparency unless that would break the upload limit; anything
 * else becomes JPEG.
 */
export async function cropImageToFile(
  src: string,
  crop: CropRect,
  spec: CoverSpec,
  original: File,
): Promise<{ file: File; dataUrl: string; width: number; height: number }> {
  const image = new Image()
  image.src = src
  await image.decode()

  const width = Math.max(1, Math.round(Math.min(crop.width, spec.recommended.width)))
  const height = Math.max(1, Math.round(width / spec.aspect))

  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const context = canvas.getContext('2d')
  if (!context) throw new Error('Canvas is not available')
  context.imageSmoothingEnabled = true
  context.imageSmoothingQuality = 'high'
  context.drawImage(image, crop.x, crop.y, crop.width, crop.height, 0, 0, width, height)

  let type = original.type === 'image/png' ? 'image/png' : 'image/jpeg'
  let blob = await canvasToBlob(canvas, type, 0.92)
  if (blob.size > MAX_UPLOAD_BYTES && type === 'image/png') {
    type = 'image/jpeg'
    blob = await canvasToBlob(canvas, type, 0.92)
  }

  const base = original.name.replace(/\.[^.]+$/, '')
  const extension = type === 'image/png' ? 'png' : 'jpg'
  const file = new File([blob], `${base}.${extension}`, { type })
  return { file, dataUrl: canvas.toDataURL(type, 0.92), width, height }
}
